import React from 'react';
import { C } from '../../theme.js';
import { SOURCE_COLUMNS } from '../../data.js';
import { ColumnsContext, OriginalColumnsContext } from '../StepInspector.jsx';
import { mergedColumns } from './columnClassification.js';

// ── Dropdown multiselect (scaling / encoding column pickers only) ──────
// Same numeric/categorical filtering as MultiCols, but rendered as a single
// closed dropdown + checklist panel instead of always-visible chips. Offers
// the MERGE of OriginalColumnsContext (the source-file schema) and the live
// per-step ColumnsContext — an original column stays offered in every
// downstream scaling/encoding step no matter what ran before it (so it can
// be re-selected after already being transformed once), AND a column an
// earlier step created (e.g. mrr_min_max_scaled) is offered too, so blocks
// can be chained (scale a column, then run Skew Correction on the scaled
// output, etc.) — see mergedColumns() for the exact merge order.
// Also excludes `__rowIndex` — a purely synthetic per-row index App.jsx
// injects, never real data, so it's never worth offering. `id`, however,
// IS filtered purely by its real type like any other column: it's commonly
// genuine numeric business data (a real dataset's own id column), not just
// App's row-diffing fallback key, so it must not be blanket-excluded.
export function MultiColsDropdown({ columns, selected = [], onChange, numeric = false, categorical = false, stringOnly = false, single = false, placeholder = 'Select columns…' }) {
  const origCols = React.useContext(OriginalColumnsContext);
  const liveCols = React.useContext(ColumnsContext);
  const ctxCols = mergedColumns(origCols, liveCols);
  const allCols = (columns || (ctxCols.length ? ctxCols : null) || SOURCE_COLUMNS);
  let displayCols = allCols;
  if (numeric) displayCols = allCols.filter(c => c.type === 'number');
  else if (categorical) displayCols = allCols.filter(c => c.type === 'string' || c.type === 'boolean');
  else if (stringOnly) displayCols = allCols.filter(c => c.type === 'string');
  if (numeric || categorical || stringOnly) {
    displayCols = displayCols.filter(c => selected.includes(c.name) || c.name !== '__rowIndex');
  }
  // A selected column that isn't in allCols at all (renamed/dropped upstream,
  // or the bookkeeping keys which allCols may not even carry a type for)
  // still needs a row to be uncheckable.
  for (const name of selected) {
    if (!displayCols.some(c => c.name === name)) {
      const known = allCols.find(c => c.name === name);
      displayCols = [...displayCols, known || { name, type: '—' }];
    }
  }

  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const toggle = (name) => {
    if (single) {
      onChange(selected.includes(name) ? [] : [name]);
      setOpen(false);
      return;
    }
    onChange(selected.includes(name) ? selected.filter(c => c !== name) : [...selected, name]);
  };
  const label = selected.length === 0 ? placeholder
    : selected.length === 1 ? selected[0]
      : `${selected.length} columns selected`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: C.input, border: `1px solid ${C.borderStrong}`, borderRadius: 8,
          padding: '8px 11px', color: selected.length ? C.fg1 : C.fg5,
          fontSize: '.85rem', fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#8ba3bf" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 8, flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 150,
          background: C.surface, border: `1px solid ${C.borderStrong}`, borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,.45)', padding: '4px 0',
          maxHeight: 220, overflowY: 'auto',
        }}>
          {displayCols.length === 0 && (
            <div style={{ padding: '8px 12px', color: C.fg5, fontSize: '.78rem' }}>No matching columns</div>
          )}
          {displayCols.map(col => {
            const on = selected.includes(col.name);
            return (
              <button
                key={col.name}
                type="button"
                onClick={() => toggle(col.name)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '7px 12px', background: 'transparent', border: 'none',
                  color: C.fg1, fontSize: '.8rem', fontFamily: 'inherit',
                  cursor: 'pointer', textAlign: 'left',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = C.input}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{
                  width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                  border: `1px solid ${on ? C.accentStrong : C.borderStrong}`,
                  background: on ? C.accentStrong : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {on && (
                    <svg viewBox="0 0 14 14" width="10" height="10" aria-hidden="true">
                      <path d="M3 7.2 5.8 10 11 4.2" fill="none" strokeWidth="2.2"
                        strokeLinecap="round" strokeLinejoin="round" stroke="#fff" />
                    </svg>
                  )}
                </span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.name}</span>
                <span style={{ fontSize: '.65rem', textTransform: 'uppercase', color: C.fg5, letterSpacing: '.05em' }}>{col.type}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
